/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/Buttons/Button';
import InputText from 'components/Inputs/inputText';
import InputRichText from 'components/Inputs/inputRichText';
import { ButtonDotsMenu } from 'xland-ui';
import useDialog from 'components/Dialog/useDialog';
import { Skeleton } from '@mui/material';
import {
  Clipboard, Award, Trash, Pencil,
  Bookmark, PlusCircle, ArrowLeft,
  FileText, ChevronDown, Calendar,
} from 'lucide-react';
import { initializeCaptable } from 'routes/Captable/modules/actions';
import { literalTemplate } from 'utils/language';
import { ROUTE_STARTUP_PATH, embedView } from 'routes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import InputPermission from 'components/Inputs/InputPermission';
import { PERMISSION_LEVELS, PERMISSION_SCOPES, PERMISSION_TYPES } from 'constants/permissions';
import { initPermission } from 'utils/functions/initPermissions';
import { TOOL_CAP_TABLE } from 'constants/tools';
import PermissionLabel from 'components/PermissionsPopup/PermissionLabel';
import OutputErrors from 'components/Inputs/outputErrors';
import { startupUserPermissionLevel } from 'utils/functions';
import InputToggle from 'components/Inputs/inputToggle';
import Portal from 'components/Portal';
import { useRect } from 'utils/customHooks';

const CaptableSelector = React.memo((props) => {
  const {
    literals,
    literalsCommon,
    user,
    captable,
    postStartupCaptable,
    putStartupCaptable,
    markStartupCaptableAsFavorite,
    deleteStartupCaptable,
    match,
    fetchCaptable,
    startup,
    editable,
  } = props;

  const POPUP_CAPTABLE = 'captable';
  const POPUP_CAPTABLE_CREATE = 'captable_create';

  const [params] = useSearchParams();
  const [selectedCaptable, setSelectedCaptable] = useState();
  const [newCaptable, setNewCaptable] = useState({
    name: '', description: '', official: false, favorite: false, valuationDiluted: false,
  });
  const [permission, setPermission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(false);
  const { dialog } = useDialog();
  const { canEdit } = permission || {};
  const toolLevel = startupUserPermissionLevel(startup, TOOL_CAP_TABLE);
  const navigate = useNavigate();

  const [popup, setPopup] = useState();
  const [bbox, ref, visible] = useRect(popup);

  useEffect(() => {
    initializeCaptable(fetchCaptable, captable, startup.id, params.get('captable'));
    if (captable) {
      setSelectedCaptable(captable.selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captable]);

  const handleCreateCaptable = async () => {
    setErrors([]);
    setLoading(true);
    const data = {
      ...newCaptable,
      permission,
    };
    try {
      if (data.id) {
        await putStartupCaptable(match.params.id, data.id, data);
      } else {
        const res = await postStartupCaptable(match.params.id, data);
        navigate({ search: `?captable=${res.id}` });
        initializeCaptable(fetchCaptable, captable, startup.id, res.id);
        setSelectedCaptable(res);
      }
      setLoading(false);
      setPopup(null);
    } catch (error) {
      setErrors(error);
      setLoading(false);
    }
  };

  const handleDeleteCaptable = async (cap) => {
    const confirm = await dialog({
      type: 'confirmDanger',
      text: literals.deleteCaptableConfirm,
    });
    if (confirm) {
      await dialog({
        type: 'loading',
        execute: async () => {
          try {
            await deleteStartupCaptable(match.params.id, cap);
          } catch (error) {
            await dialog({
              type: 'error',
              text: <OutputErrors errors={error} text />,
            });
          }
        },
      });
    }
  };

  const handleOfficialCaptable = async (cap) => {
    const confirm = await dialog({
      type: 'confirm',
      text: literals.officialConfirm,
    });
    if (confirm) {
      await dialog({
        type: 'loading',
        execute: async () => {
          try {
            await putStartupCaptable(match.params.id, cap.id, { ...cap, official: true });
          } catch (error) {
            await dialog({
              type: 'error',
              text: <OutputErrors errors={error} text />,
            });
          }
        },
      });
    }
  };

  const handleFavoriteCaptable = async (cap) => {
    const confirm = await dialog({
      type: 'confirm',
      text: literals.favoriteConfirm,
    });
    if (confirm) {
      await dialog({
        type: 'loading',
        execute: async () => {
          try {
            await markStartupCaptableAsFavorite(match.params.id, cap.id);
          } catch (error) {
            await dialog({
              type: 'error',
              text: <OutputErrors errors={error} text />,
            });
          }
        },
      });
    }
  };

  const renderOperations = (cap) => (
    <div
      key={cap.id}
      className={`captable-popup-item ${cap.id === selectedCaptable?.id ? 'selected' : ''}`}
      onClick={() => {
        navigate({ search: `?captable=${cap.id}` });
        setSelectedCaptable(cap);
        initializeCaptable(fetchCaptable, captable, startup.id, cap.id);
        setPopup(null);
      }}
    >
      <div className='d-flex align-items-center justify-content-between'>
        <div className='captable-popup-item--left'>
          <div className='captable-popup-item--icon'><Clipboard size={14} /></div>
          <h6 className='captable-popup-item--name' title={cap.name}>{cap.name}</h6>
          {cap.official && <Award size={14} />}
          {cap?.favorite === user.id && <Bookmark size={14} />}
          <PermissionLabel permission={cap.permission} />
        </div>
      </div>
      {editable && (
        <div>
          <ButtonDotsMenu
            key={`menu-document-${document.id}`}
            size={20}
            buttons={[
              {
                text: literalsCommon.edit,
                icon: Pencil,
                onClick: () => {
                  setNewCaptable(cap);
                  setPermission(initPermission(
                    cap,
                    PERMISSION_SCOPES.STARTUP,
                    startup,
                    TOOL_CAP_TABLE,
                  ));
                  setPopup(POPUP_CAPTABLE_CREATE);
                },
              },
              {
                text: literals.official,
                icon: Award,
                onClick: () => handleOfficialCaptable(cap),
                hidden: toolLevel < PERMISSION_LEVELS.EDIT,
                disabled: cap.official,
              },
              {
                text: literals.favorite,
                icon: Bookmark,
                onClick: () => handleFavoriteCaptable(cap),
                hidden: toolLevel !== PERMISSION_LEVELS.EXTERNAL,
                disabled: cap.favorite,
              },
              {
                text: literalsCommon.delete,
                icon: Trash,
                mode: 'danger',
                onClick: () => handleDeleteCaptable(cap.id),
                disabled: cap.official,
                hidden: cap.permission.level < PERMISSION_LEVELS.EDIT,
              },
            ]}
          />
        </div>
      )}
    </div>
  );

  if (captable.loaded && !captable?.captables?.length) {
    return null;
  }

  const styles = { visible };
  if (bbox) {
    styles.top = bbox.bottom + 5;
    styles.left = bbox.left;
  }

  return (
    <>
      <div
        ref={ref}
        className='captable-selector'
        onClick={() => setPopup(POPUP_CAPTABLE)}
      >
        <span className='captable-selector--left'>
          <FileText size={16} />
          {captable?.loaded ? (
            selectedCaptable ? (
              <>
                <div className='captable-selector--name'>{selectedCaptable ? selectedCaptable.name : ''}</div>
                {selectedCaptable?.official && <Award size={16} />}
                {selectedCaptable?.favorite === user.id && <Bookmark size={16} />}
              </>
            ) : literals.noCaptables
          ) : (
            <Skeleton width='80px' />
          )}
        </span>
        <ChevronDown size={16} />
      </div>

      {popup === POPUP_CAPTABLE && (
        <Portal styles={styles} onClose={() => setPopup(null)}>
          <div className='captable-popup'>
            {editable && (
              <div className='captable-popup-buttons'>
                <Button
                  iconRight={Calendar}
                  color='secondary'
                  text={literals.moreDetails}
                  onClick={() => navigate(embedView(ROUTE_STARTUP_PATH.setCapTableCaptables(match.params.id)))}
                />
                <Button
                  iconRight={PlusCircle}
                  text={literalsCommon.create}
                  onClick={() => {
                    setNewCaptable({ name: '', description: '' });
                    setPermission(initPermission(
                      null,
                      PERMISSION_SCOPES.STARTUP,
                      startup,
                      TOOL_CAP_TABLE,
                    ));
                    setPopup(POPUP_CAPTABLE_CREATE);
                  }}
                />
              </div>
            )}
            {captable?.captables?.length ? (
              <>
                <h6>{literals.captables}</h6>
                <div className='captable-popup-items simple-scrollbar'>
                  {captable.captables.map(renderOperations)}
                </div>
              </>
            ) : null}
          </div>
        </Portal>
      )}

      {
        popup === POPUP_CAPTABLE_CREATE && (
          <Portal styles={styles} onClose={() => setPopup(null)}>
            <div className='captable-popup'>
              <InputPermission
                scope={{ type: PERMISSION_SCOPES.STARTUP, id: startup.id }}
                element={{ type: PERMISSION_TYPES.CAPTABLE, id: captable?.id }}
                value={permission}
                onChange={setPermission}
              />
              <InputText
                preText={literalsCommon.name}
                value={newCaptable.name}
                onChange={v => setNewCaptable(prev => ({ ...prev, name: v }))}
                isDisabled={!canEdit}
                postText={literalTemplate(literals.maxCharacters, { NUMBER: 50 })}
              />
              <div>
                {
                  toolLevel >= PERMISSION_LEVELS.EDIT && (
                    <InputToggle
                      className='justify-content-between m-0'
                      preText={literals.official}
                      value={newCaptable.official}
                      onChange={official => setNewCaptable(prev => ({ ...prev, official }))}
                    />
                  )
                }
                {
                  toolLevel === PERMISSION_LEVELS.EXTERNAL && (
                    <InputToggle
                      className='justify-content-between m-0'
                      preText={literals.favorite}
                      value={newCaptable.favorite}
                      onChange={favorite => setNewCaptable(prev => ({ ...prev, favorite }))}
                    />
                  )
                }
              </div>

              <div>
                <InputToggle
                  className='justify-content-between'
                  preText={literals.valuationDiluted}
                  value={newCaptable?.valuationDiluted}
                  onChange={valuationDiluted => setNewCaptable(prev => ({ ...prev, valuationDiluted }))}
                  isDisabled={!canEdit}
                />
              </div>

              <InputRichText
                preText={literalsCommon.description}
                value={newCaptable.description}
                onChange={v => setNewCaptable(prev => ({ ...prev, description: v }))}
                isDisabled={!canEdit}
              />
              <OutputErrors errors={errors} />
              {canEdit && (
                <div className='buttons'>
                  <Button
                    color='secondary'
                    icon={ArrowLeft}
                    text={literalsCommon.back}
                    onClick={() => setPopup(POPUP_CAPTABLE)}
                    loading={loading}
                  />
                  <Button
                    icon={newCaptable.id ? Pencil : PlusCircle}
                    text={newCaptable.id ? literalsCommon.edit : literalsCommon.create}
                    onClick={handleCreateCaptable}
                    loading={loading}
                  />
                </div>
              )}
            </div>
            <OutputErrors errors={errors} />
          </Portal>
        )
      }
    </>
  );
});

CaptableSelector.propTypes = {
  literals: PropTypes.object.isRequired,
  literalsCommon: PropTypes.object.isRequired,
  postStartupCaptable: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  editable: PropTypes.bool,
  user: PropTypes.object.isRequired,
  captable: PropTypes.object.isRequired,
  startup: PropTypes.object.isRequired,
  putStartupCaptable: PropTypes.func.isRequired,
  markStartupCaptableAsFavorite: PropTypes.func.isRequired,
  deleteStartupCaptable: PropTypes.func.isRequired,
  fetchCaptable: PropTypes.func.isRequired,
};

CaptableSelector.defaultProps = {
  editable: true,
};

export default CaptableSelector;
